/**
 * Voice store.
 *
 * Owns microphone-recording state as a single shared session — both the
 * `InputBar` mic button (press-and-hold) and the global push-to-talk hotkey
 * (`AppLayout`, bound to `settingsStore.pushToTalkHotkey`) start/stop the
 * *same* recording rather than each holding independent local state, so the
 * mic indicator stays correct no matter which trigger was used.
 */
import { create } from "zustand";
import { voiceService, VoiceServiceError } from "@/services/voice";
import { logger } from "@/services/logger";
import { useAppStore } from "./appStore";

const log = logger.scope("voice-store");

interface VoiceState {
  isRecording: boolean;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  toggleRecording: () => void;
}

let mediaRecorder: MediaRecorder | null = null;
let chunks: Blob[] = [];
let stream: MediaStream | null = null;

async function handleRecordingComplete(): Promise<void> {
  const audio = new Blob(chunks, { type: "audio/webm" });
  chunks = [];
  if (audio.size === 0) return;

  try {
    const text = await voiceService.transcribe({ audio });
    useAppStore.getState().setPendingComposerText(text);
  } catch (err) {
    if (err instanceof VoiceServiceError && err.kind === "not-implemented") {
      useAppStore.getState().addToast({
        title: "Voice input isn't set up yet",
        description: "Local speech-to-text is planned but not installed in this build.",
        variant: "default",
      });
      return;
    }
    log.error("Transcription failed", err);
    useAppStore.getState().addToast({ title: "Couldn't transcribe recording", variant: "error" });
  }
}

export const useVoiceStore = create<VoiceState>()((set, get) => ({
  isRecording: false,

  startRecording: async () => {
    if (get().isRecording) return;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      log.warn("Microphone access denied or unavailable", err);
      useAppStore.getState().addToast({
        title: "Couldn't access the microphone",
        description: "Check the app's microphone permission and try again.",
        variant: "error",
      });
      return;
    }

    chunks = [];
    mediaRecorder = new MediaRecorder(stream);
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };
    mediaRecorder.onstop = () => {
      void handleRecordingComplete();
    };
    mediaRecorder.start();
    set({ isRecording: true });
  },

  stopRecording: () => {
    mediaRecorder?.stop();
    stream?.getTracks().forEach((track) => track.stop());
    mediaRecorder = null;
    stream = null;
    set({ isRecording: false });
  },

  toggleRecording: () => {
    if (get().isRecording) {
      get().stopRecording();
    } else {
      void get().startRecording();
    }
  },
}));
